import { makeAutoObservable, runInAction } from 'mobx'
import {
  parseWidgetsAndChannelsFromApi,
  type ReportChannelOption,
  type WidgetListItem,
} from '@/api/reportsApi'
import { handleUnauthorized } from '@utils/apiHelpers'
import { buildWidgetListUrl } from '@utils/widgetListApi'

const WIDGET_LIST_LIMIT = 100

export class UserWidgetsStore {
  widgets: WidgetListItem[] = []
  channels: ReportChannelOption[] = []
  /** Raw GET /api/widget items — enough for /control to paginate without a second fetch. */
  listItems: unknown[] = []
  listTotal = 0
  loading = false
  loaded = false
  /** True only after a successful list response (empty catalog counts as success). */
  loadedOk = false

  private loadPromise: Promise<void> | null = null
  private cacheKey: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  private sessionKey(baseUrl: string, headers: Record<string, string>): string {
    return `${baseUrl.replace(/\/$/, '')}|${headers.Authorization ?? ''}`
  }

  /** Один запрос GET /api/widget на сессию; повторные вызовы возвращают тот же promise. */
  ensureLoaded(baseUrl: string, headers: Record<string, string>): Promise<void> {
    const key = this.sessionKey(baseUrl, headers)
    if (this.loaded && this.cacheKey === key) {
      return Promise.resolve()
    }
    if (this.loadPromise && this.cacheKey === key) {
      return this.loadPromise
    }
    return this.fetch(baseUrl, headers, key, false)
  }

  /** Принудительное обновление (после добавления канала и т.п.). */
  refresh(baseUrl: string, headers: Record<string, string>): Promise<void> {
    const key = this.sessionKey(baseUrl, headers)
    if (this.loadPromise && this.cacheKey === key) {
      return this.loadPromise
    }
    this.loaded = false
    this.loadedOk = false
    return this.fetch(baseUrl, headers, key, true)
  }

  canServePage(page: number, limit: number): boolean {
    if (!this.loadedOk) return false
    const start = Math.max(0, (Math.max(1, page) - 1) * Math.max(1, limit))
    if (this.listTotal <= this.listItems.length) return true
    if (start >= this.listTotal) return true
    return start + Math.max(1, limit) <= this.listItems.length
  }

  pageItems(page: number, limit: number): unknown[] {
    const size = Math.max(1, limit)
    const start = Math.max(0, (Math.max(1, page) - 1) * size)
    return this.listItems.slice(start, start + size)
  }

  private fetch(
    baseUrl: string,
    headers: Record<string, string>,
    key: string,
    force: boolean,
  ): Promise<void> {
    if (this.loadPromise && this.cacheKey === key) {
      return this.loadPromise
    }
    if (!force && this.loaded && this.cacheKey === key) {
      return Promise.resolve()
    }

    const base = baseUrl.replace(/\/$/, '')
    if (!base) return Promise.resolve()

    this.cacheKey = key
    this.loading = true

    this.loadPromise = (async () => {
      let ok = false
      try {
        const res = await fetch(buildWidgetListUrl(base, { page: 1, limit: WIDGET_LIST_LIMIT }), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', ...headers },
        })
        if (handleUnauthorized(res)) return
        if (!res.ok) {
          const errBody = await res.text().catch(() => '')
          console.error('[UserWidgetsStore] GET /api/widget failed:', res.status, errBody)
          return
        }
        const json = await res.json()
        const parsed = parseWidgetsAndChannelsFromApi(json)
        const items = Array.isArray(json?.data?.items) ? json.data.items : []
        const totalRaw = Number(json?.data?.total)
        const total = Number.isFinite(totalRaw) ? Math.max(0, Math.trunc(totalRaw)) : items.length
        ok = json?.success === true
        runInAction(() => {
          this.widgets = parsed.widgets
          this.channels = parsed.channels
          this.listItems = items
          this.listTotal = total
        })
      } catch (err) {
        console.error('[UserWidgetsStore] GET /api/widget failed:', err)
      } finally {
        runInAction(() => {
          this.loading = false
          this.loaded = true
          this.loadedOk = ok
          this.loadPromise = null
        })
      }
    })()

    return this.loadPromise
  }

  reset() {
    this.widgets = []
    this.channels = []
    this.listItems = []
    this.listTotal = 0
    this.loading = false
    this.loaded = false
    this.loadedOk = false
    this.loadPromise = null
    this.cacheKey = null
  }
}
